import { useState } from 'react';
import { ModuleType } from 'infrastructure/enums';
import common from 'constants/common';

import type {
  ICcmOnboardingStatus,
  ICcmOnboardingStatusResponse,
  IRpmOnboardingStatus,
  IRpmOnboardingStatusResponse,
} from 'infrastructure/interfaces';

export type StatusBlocks =
  | ICcmOnboardingStatusResponse['statuses']
  | IRpmOnboardingStatusResponse['statuses'];

interface ChartItem {
  angle: number;
  label: string;
  color: string;
  value: {
    Status: string;
    Patients: number;
  };
}

export type TCcmKey = keyof ICcmOnboardingStatus;
export type TRpmKey = keyof IRpmOnboardingStatus;

export const useFulfillmentData = (
  module: ModuleType,
  statusBlocks?: StatusBlocks,
) => {
  const [disabledKeys, setDisabledKeys] = useState<string[]>([]);
  const [hintState, setHintState] = useState({
    show: false,
    value: {},
  });
  let keys: TRpmKey[] | TCcmKey[] = [];
  let chartData: ChartItem[] = [];

  if (
    module === ModuleType.CCM &&
    statusBlocks &&
    'CCM Onboarding' in statusBlocks
  ) {
    const ccmStatuses = statusBlocks as ICcmOnboardingStatus;
    keys = Object.keys(ccmStatuses).filter(
      (item) =>
        item !== 'Total CCM Patients' && ccmStatuses[item as TCcmKey] > 0,
    ) as (keyof typeof ccmStatuses)[];

    chartData = keys
      .filter((key) => !disabledKeys.includes(key))
      .map((key) => ({
        angle: parseInt(String(ccmStatuses[key]), 10),
        label: common.ccmStatusNames[key].label,
        color: common.ccmStatusNames[key].color,
        value: {
          Status: common.ccmStatusNames[key].label,
          Patients: ccmStatuses[key],
        },
      }));
  }

  if (
    module === ModuleType.RPM &&
    statusBlocks &&
    'onboarding' in statusBlocks
  ) {
    const rpmStatuses = statusBlocks as IRpmOnboardingStatus;
    keys = Object.keys(rpmStatuses).filter(
      (item) => item !== 'total' && rpmStatuses[item as TRpmKey] > 0,
    ) as (keyof typeof rpmStatuses)[];

    chartData = keys
      .filter((key) => !disabledKeys.includes(key))
      .map((key) => ({
        angle: parseInt(String(rpmStatuses[key]), 10),
        label: common.statusNames[key].label,
        color: common.statusNames[key].color,
        value: {
          Status: common.statusNames[key].label,
          Patients: rpmStatuses[key],
        },
      }));
  }

  return {
    keys,
    chartData,
    disabledKeys,
    setDisabledKeys,
    hintState,
    setHintState,
  };
};
